import api from '../../../api';

const REPORTS_ANALYSIS_API = '/sales-report/reports-analysis';

/**
 * Annual revenue projection (Analysis tab).
 * Backend: AnnualRevenueProjectionController — /api/crm/annual-revenue-projection + GET /analysis.
 * Axios baseURL is REACT_APP_API_ENDPOINT + '/api', so this path is the segment after /api.
 */
const ANNUAL_REVENUE_PROJECTION_ANALYSIS_API = '/crm/annual-revenue-projection/analysis';

const PROJECTION_YEARS_LENGTH = 5;

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DEFAULT_CHART_COLORS = ['#206bc4', '#2fb344', '#f59f00', '#ae3ec9', '#e03131', '#7950f2'];

function num(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * GET /sales-report/reports-analysis — Total Proposal card on Analysis Overview.
 * Contract: see reports-analysis-overview-payload.example.json
 */
function normalizeTotalProposalSummary(rawRoot, proposalFunnel) {
  const tps = rawRoot?.totalProposalSummary;
  if (tps && Array.isArray(tps.breakdown) && tps.breakdown.length > 0) {
    const breakdown = tps.breakdown.map((row) => ({
      label: String(row?.label ?? '').trim() || '—',
      value: num(row?.value)
    }));
    const sumBreakdown = breakdown.reduce((s, r) => s + r.value, 0);
    const explicit = num(tps.total);
    const total = explicit > 0 ? explicit : sumBreakdown;
    return { total, breakdown };
  }
  const labels = Array.isArray(proposalFunnel?.labels) ? proposalFunnel.labels : [];
  const data = Array.isArray(proposalFunnel?.data) ? proposalFunnel.data : [];
  const breakdown = labels.map((label, i) => ({
    label: String(label ?? '').trim() || '—',
    value: num(data[i])
  }));
  const total = breakdown.reduce((s, r) => s + r.value, 0);
  return { total, breakdown };
}

/**
 * PIC Performance Analysis row. Backend may use forecastM1 / negotiation etc.; we normalize to UI fields.
 * Contract: see reports-analysis-overview-payload.example.json
 */
function normalizePicPerformanceRow(raw) {
  return {
    name: String(raw?.name ?? '').trim(),
    total: num(raw?.total),
    open: num(raw?.open),
    m1: num(raw?.m1 ?? raw?.forecastM1),
    m2: num(raw?.m2 ?? raw?.forecastM2),
    m3: num(raw?.m3 ?? raw?.forecastM3),
    nego: num(raw?.nego ?? raw?.negotiation),
    won: num(raw?.won),
    lost: num(raw?.lost),
    winRate: num(raw?.winRate)
  };
}

/**
 * Backend sends `yearValues` as { "2025": amount, ... } on detailed lost / all-stages rows.
 * Flatten to `year_<Y>` for table columns; keep legacy flat `year_*` if already present.
 */
function flattenDetailedYearRow(row, years) {
  if (!row || typeof row !== 'object') return row;
  const yrList = Array.isArray(years) && years.length > 0 ? years : getProjectionYears();
  const out = { ...row };
  const yv = row.yearValues;
  yrList.forEach((y) => {
    const flatKey = `year_${y}`;
    let v;
    if (yv && typeof yv === 'object' && !Array.isArray(yv)) {
      const raw = yv[String(y)];
      v = raw !== undefined && raw !== null ? raw : yv[y];
    }
    if (v !== undefined && v !== null) {
      out[flatKey] = num(v);
    } else if (out[flatKey] == null || out[flatKey] === '') {
      out[flatKey] = 0;
    }
  });
  return out;
}

/**
 * Extract ID from filter value (handles both primitive ids and objects with id/value).
 */
function toId(x) {
  if (x == null || x === '' || x === 'all') return null;
  if (typeof x === 'object' && (x.id != null || x.value != null)) return String(x.id ?? x.value ?? '');
  return String(x);
}

/**
 * Build query params for reports-analysis API. Backend should accept these and return the analysis payload.
 */
export function buildAnalysisPayload(filters, customDateRange, viewParams = {}) {
  const params = {};
  // Always send period (including `all`) so backends can distinguish “all time” from a missing param.
  params.period = filters.period != null && filters.period !== '' ? filters.period : 'this_year';
  if (filters.period === 'custom' && customDateRange?.startDate) params.startDate = customDateRange.startDate;
  if (filters.period === 'custom' && customDateRange?.endDate) params.endDate = customDateRange.endDate;
  const toParam = (v) => (Array.isArray(v) ? v.map(toId).filter((x) => x != null && x !== '') : (toId(v) ? [toId(v)] : []));
  const join = (arr) => (arr.length === 0 ? undefined : arr.join(','));
  const salesCreator = join(toParam(filters.salesCreator));
  const department = join(toParam(filters.department));
  const sector = join(toParam(filters.sector));
  const leadSource = join(toParam(filters.leadSource));
  const branchId = toId(filters.branchId);
  // UI uses `standard`; keep `service` as backward-compatible fallback.
  const service = join(toParam(filters.standard ?? filters.service));
  const proposalStage = join(toParam(filters.proposalStage));
  const competitor = join(toParam(filters.competitor));
  const lostReason = join(toParam(filters.lostReason));
  const taggingIds = join(toParam(filters.taggingIds));
  if (salesCreator) params.salesCreator = salesCreator;
  if (department) params.department = department;
  if (sector) params.sector = sector;
  if (leadSource) params.leadSource = leadSource;
  if (branchId) params.branchId = branchId;
  if (service) params.service = service;
  if (proposalStage) params.proposalStage = proposalStage;
  if (competitor) params.competitor = competitor;
  if (lostReason) params.lostReason = lostReason;
  if (taggingIds) params.taggingIds = taggingIds;
  if (filters.proposalApprovalStatus && filters.proposalApprovalStatus !== 'all') params.proposalApprovalStatus = filters.proposalApprovalStatus;
  if (viewParams.viewOwnUserId != null) params.viewOwnUserId = viewParams.viewOwnUserId;
  if (viewParams.viewDepartmentId != null) {
    params.viewDepartmentId = viewParams.viewDepartmentId;
    params.department = String(viewParams.viewDepartmentId);
  }
  // Optional: allow backend currency filtering when provided via viewParams.currencyId
  if (viewParams.currencyId != null) {
    params.currencyId = viewParams.currencyId;
  }
  return params;
}

/**
 * Expected response shape from GET reports-analysis (for backend implementation).
 * All fields optional; frontend normalizes to these shapes with empty/zero defaults.
 *
 * kpi: { totalProspects, proposalWinRate, totalWonValue, totalExpenses }
 * wonValueByDepartment: { months?: string[], series: [{ name, data: number[], color? }] }
 * lostRateByDepartment: { months?: string[], series: [{ name, data: number[], color? }] }
 * proposalFunnel: { labels: string[], data: number[] }
 * prospectsByLeadSource: { labels: string[], data: number[] }
 * productWinRate: { labels: string[], data: number[] }
 * topSectors: [{ sector: string, proposals: number }]
 * topPerformers: [{ name: string, won: number, value: number }]
 * totalProposalSummary: { total?: number, breakdown: [{ label: string, value: number }] } — Overview “Total Proposal” card; if omitted, UI falls back to proposalFunnel labels/data
 * picPerformance: [{ name, total, open, m1, m2, m3, nego, won, lost, winRate }] — stage counts from API (aliases forecastM1–M3, negotiation accepted)
 * competitorAnalysis: [{ competitor: string, faced: number, ourWins: number, ourLosses: number }]
 * lostValueByReason: { labels: string[], data: number[] }
 * lostCountByReason: { labels: string[], data: number[] }
 * lostCountByCompetitor: [{ competitor: string, lostCount: number, totalLostValue: number }]
 * projectionYears: number[] — years for detailed table columns (from backend `projectionYears`; default 5 years from current year)
 * detailedLostProposals: rows may include `yearValues` map { "2025": amount } or flat `year_2025`; normalized to `year_<Y>` keys
 * detailedProposals: same year breakdown for “Detailed Proposal Data (All Stages)”
 * annualRevenueProjection: { years: number[], series: [{ name, data: number[], color? }] } — 5 years from current year
 * detailedAnnualBreakdown: [{ id?, leadId?, proposalNo, client, totalValue, year_<year>, ... }] — year_<year> per projection year
 * filterOptions: { sources: [{ id, name }], businessUnits: [{ id, name }], sectors: [{ id, name }], standards: [{ id, name }], salesUsers: [{ id, name }], proposalStages: [{ id, name }] }
 */
export function normalizeAnalysisResponse(raw) {
  const d = raw || {};
  const kpi = d.kpi || {};
  const fo = d.filterOptions || {};
  const ensureArray = (arr) => (Array.isArray(arr) ? arr : []);
  const ensureSeries = (s, colors = DEFAULT_CHART_COLORS) =>
    (Array.isArray(s) ? s : []).map((item, i) => ({
      name: item.name || '',
      data: Array.isArray(item.data) ? item.data : [],
      color: item.color || colors[i % colors.length]
    }));
  const projectionYearsResolved =
    Array.isArray(d.projectionYears) && d.projectionYears.length > 0
      ? d.projectionYears.map((y) => Number(y))
      : getProjectionYears();
  const funnelLabels = Array.isArray(d.proposalFunnel?.labels) ? d.proposalFunnel.labels : [];
  const funnelData = Array.isArray(d.proposalFunnel?.data) ? d.proposalFunnel.data : [];
  const proposalFunnel = { labels: funnelLabels, data: funnelData };
  const totalProposalSummary = normalizeTotalProposalSummary(d, proposalFunnel);
  const picPerformanceNormalized = ensureArray(d.picPerformance).map(normalizePicPerformanceRow);

  return {
    kpi: {
      totalProspects: Number(kpi.totalProspects) || 0,
      proposalWinRate: Number(kpi.proposalWinRate) || 0,
      totalWonValue: Number(kpi.totalWonValue) || 0,
      totalExpenses: Number(kpi.totalExpenses) || 0
    },
    wonValueByDepartment: {
      months: Array.isArray(d.wonValueByDepartment?.months) ? d.wonValueByDepartment.months : MONTHS,
      series: ensureSeries(d.wonValueByDepartment?.series)
    },
    lostRateByDepartment: {
      months: Array.isArray(d.lostRateByDepartment?.months) ? d.lostRateByDepartment.months : MONTHS,
      series: ensureSeries(d.lostRateByDepartment?.series)
    },
    proposalFunnel,
    totalProposalSummary,
    prospectsByLeadSource: {
      labels: Array.isArray(d.prospectsByLeadSource?.labels) ? d.prospectsByLeadSource.labels : [],
      data: Array.isArray(d.prospectsByLeadSource?.data) ? d.prospectsByLeadSource.data : []
    },
    productWinRate: {
      labels: Array.isArray(d.productWinRate?.labels) ? d.productWinRate.labels : [],
      data: Array.isArray(d.productWinRate?.data) ? d.productWinRate.data : []
    },
    topSectors: Array.isArray(d.topSectors) ? d.topSectors : [],
    topPerformers: Array.isArray(d.topPerformers) ? d.topPerformers : [],
    picPerformance: picPerformanceNormalized,
    competitorAnalysis: Array.isArray(d.competitorAnalysis) ? d.competitorAnalysis : [],
    lostValueByReason: {
      labels: Array.isArray(d.lostValueByReason?.labels) ? d.lostValueByReason.labels : [],
      data: Array.isArray(d.lostValueByReason?.data) ? d.lostValueByReason.data : []
    },
    lostCountByReason: {
      labels: Array.isArray(d.lostCountByReason?.labels) ? d.lostCountByReason.labels : [],
      data: Array.isArray(d.lostCountByReason?.data) ? d.lostCountByReason.data : []
    },
    lostCountByCompetitor: Array.isArray(d.lostCountByCompetitor) ? d.lostCountByCompetitor : [],
    projectionYears: projectionYearsResolved,
    detailedLostProposals: ensureArray(d.detailedLostProposals).map((row) =>
      flattenDetailedYearRow(row, projectionYearsResolved)
    ),
    detailedProposals: ensureArray(d.detailedProposals).map((row) =>
      flattenDetailedYearRow(row, projectionYearsResolved)
    ),
    annualRevenueProjection: d.annualRevenueProjection && Array.isArray(d.annualRevenueProjection?.years) && Array.isArray(d.annualRevenueProjection?.series)
      ? { years: d.annualRevenueProjection.years, series: ensureSeries(d.annualRevenueProjection.series) }
      : null,
    detailedAnnualBreakdown: Array.isArray(d.detailedAnnualBreakdown) ? d.detailedAnnualBreakdown : [],
    filterOptions: {
      sources: ensureArray(fo.sourceOptions ?? fo.sources),
      businessUnits: ensureArray(fo.businessUnits),
      sectors: ensureArray(fo.sectors),
      standards: ensureArray(fo.standards),
      salesUsers: ensureArray(fo.salesUsers),
      branches: ensureArray(fo.branches),
      taggings: ensureArray(fo.taggings ?? fo.taggingOptions),
      proposalStages: ensureArray(fo.proposalStages)
    }
  };
}

/**
 * Fetch analysis data from reports-analysis API. Returns a promise that resolves to normalized analysis data.
 */
/**
 * Fetch analysis data. Single API: GET /reports-analysis with optional query params.
 * Response includes analysis data and filterOptions for dropdowns.
 */
export function getAnalysis(params) {
  return api.get(REPORTS_ANALYSIS_API, { params }).then((res) => normalizeAnalysisResponse(res.data));
}

/** Five projection years starting from the current calendar year (aligns with backend PROJECTION_LENGTH). */
export function getProjectionYears() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: PROJECTION_YEARS_LENGTH }, (_, i) => currentYear + i);
}

function arpNum(v) {
  if (v == null) return 0;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

function ensureArpSeries(series) {
  return (Array.isArray(series) ? series : []).map((item) => ({
    name: item.name || '',
    data: (Array.isArray(item.data) ? item.data : []).map(arpNum),
    color: item.color || undefined
  }));
}

function arpBreakdownRowToFlat(row, years) {
  const out = {
    id: row.id,
    leadId: row.leadId,
    proposalNo: row.proposalNo ?? '',
    client: row.client ?? '',
    totalValue: arpNum(row.totalValue)
  };
  const yv = row.yearValues && typeof row.yearValues === 'object' ? row.yearValues : {};
  (years || []).forEach((y) => {
    const yStr = String(y);
    const flatKey = `year_${y}`;
    out[flatKey] = arpNum(yv[yStr] ?? yv[flatKey] ?? 0);
  });
  return out;
}

/**
 * Normalize GET annual-revenue-projection/analysis JSON.
 * detailedAnnualBreakdown is paginated: { items, totalItems, totalPages, currentPage, pageSize }.
 * Rows use yearValues map (e.g. "2026"); flattened to year_2026 for the table.
 */
export function normalizeAnnualRevenueProjectionResponse(raw) {
  const d = raw || {};
  const kpi = d.kpi || {};
  const arp = d.annualRevenueProjection || {};
  const years =
    Array.isArray(arp.years) && arp.years.length > 0 ? arp.years.map((y) => Number(y)) : null;

  const breakdownRaw = d.detailedAnnualBreakdown;
  let paginated = {
    items: [],
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
    grandTotalValue: 0,
    grandTotalsByYear: null
  };

  const parseGrandByYear = (raw, yrList) => {
    if (!raw || typeof raw !== 'object') return null;
    const out = {};
    (yrList || []).forEach((y) => {
      out[String(y)] = arpNum(raw[String(y)]);
    });
    return out;
  };

  if (breakdownRaw && Array.isArray(breakdownRaw.items)) {
    paginated = {
      items: breakdownRaw.items.map((row) => arpBreakdownRowToFlat(row, years || [])),
      totalItems: arpNum(breakdownRaw.totalItems),
      totalPages: Math.max(0, arpNum(breakdownRaw.totalPages)),
      currentPage: Math.max(1, arpNum(breakdownRaw.currentPage) || 1),
      pageSize: Math.max(1, arpNum(breakdownRaw.pageSize) || 10),
      grandTotalValue: arpNum(breakdownRaw.grandTotalValue),
      grandTotalsByYear: parseGrandByYear(breakdownRaw.grandTotalsByYear, years)
    };
  } else if (Array.isArray(breakdownRaw)) {
    const items = breakdownRaw.map((row) => arpBreakdownRowToFlat(row, years || []));
    let grandTotalValue = 0;
    const grandTotalsByYear = {};
    (years || []).forEach((y) => {
      grandTotalsByYear[String(y)] = 0;
    });
    items.forEach((row) => {
      grandTotalValue += arpNum(row.totalValue);
      (years || []).forEach((y) => {
        const flatKey = `year_${y}`;
        grandTotalsByYear[String(y)] += arpNum(row[flatKey]);
      });
    });
    paginated = {
      items,
      totalItems: items.length,
      totalPages: items.length ? 1 : 0,
      currentPage: 1,
      pageSize: items.length || 10,
      grandTotalValue,
      grandTotalsByYear
    };
  }

  return {
    kpi: {
      totalProspects: arpNum(kpi.totalProspects),
      proposalWinRate: arpNum(kpi.proposalWinRate),
      totalWonValue: arpNum(kpi.totalWonValue),
      totalExpenses: arpNum(kpi.totalExpenses)
    },
    topPerformers: Array.isArray(d.topPerformers)
      ? d.topPerformers.map((p) => ({
          name: p.name || '',
          won: arpNum(p.won),
          value: arpNum(p.value)
        }))
      : [],
    annualRevenueProjection:
      years && Array.isArray(arp.series)
        ? { years, series: ensureArpSeries(arp.series) }
        : null,
    detailedAnnualBreakdownPaginated: paginated
  };
}

/**
 * Same filter/query params as reports-analysis, plus pagination (and optional currencyId).
 * Uses buildAnnualRevenueProjectionParams from UI state.
 */
export function buildAnnualRevenueProjectionParams(filters, customDateRange, viewParams = {}, pagination = {}) {
  const base = buildAnalysisPayload(filters, customDateRange, viewParams);
  return {
    ...base,
    page: pagination.page ?? 1,
    size: pagination.size ?? 10
  };
}

/**
 * @param {Record<string, unknown>} params — from buildAnnualRevenueProjectionParams + optional currencyId
 */
export function getAnnualRevenueProjectionAnalysis(params = {}) {
  return api
    .get(ANNUAL_REVENUE_PROJECTION_ANALYSIS_API, { params })
    .then((res) => normalizeAnnualRevenueProjectionResponse(res.data));
}
